/* eslint no-param-reassign:0 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { EligibilityVerificationExternalFrag } from '@valerahealth/rtk-query'
import { SYSTEM_US_TIMEZONE } from '@valerahealth/ui-components/utils/date'
import {
  type PatientOnboardingFormRoutePath,
  type ReferralFormRoutePath,
} from '../routes/routeConfig'

// keep consistent and make human readable
export const getVerificationKey = ({
  idNumber,
  insurancePlanId,
}: {
  idNumber: string
  insurancePlanId: string
}) => `InsurancePlanId#${insurancePlanId}_MemberNumber#${idNumber}`

export type OptionalOnboardingFormSection = Exclude<
  PatientOnboardingFormRoutePath | ReferralFormRoutePath,
  | '*'
  | 'referrar-information'
  | 'progress/patient-information'
  | 'progress/patient-information/verify'
  | 'complete'
>

export type InsuranceVerificationStatus =
  | 'active' // set when billing indicates insurance is active
  | 'pending' // fallback due to api error or pverify timeout, requires manual verification
  | 'error' // catch-all error state, usually a eligibility reason is provided by pverify
  
  // specific error carve outs
  | 'inactive'
  | 'subscriberUnknown'


export const getVerificationStatus = (v: EligibilityVerificationExternalFrag | null): InsuranceVerificationStatus => {
  
  if (v?.isActive) return 'active'
  if (!v?.eligibilityReason) return 'inactive'

  switch (v?.eligibilityReason) {
    case 'Unable to respond Eligibility Verification record':
      return 'pending'
    case 'Invalid/Missing Subscriber/Insured ID':
    case 'Subscriber/Insured Not Found':
    case 'Subscriber / Insured Not Found':
    case 'Invalid/Missing Subscriber/Insured Name':
      return 'subscriberUnknown'
    default:
      return 'error'
  }
}

export type InsuranceVerification = {
  status: InsuranceVerificationStatus
  billingInsured?: EligibilityVerificationExternalFrag
}

export type InsuranceVerificationState = {
  // ultimately, verification is based on only two datapoints... Insurance ID and Member ID. If a user supplies the two we can display
  [verificationKey: string]: InsuranceVerification
}

export type AppState = {
  onboarding: {
    returnUrl?: string | null
    returnType?: 'mobile' | 'external' | null
    submissionId?: string | null
    captchaToken?: string | null
    optionalOnboardingFormSections?: OptionalOnboardingFormSection[]
    insuranceVerificationState: InsuranceVerificationState
    timeZone : string
    programCode?: string | null
    formsApiEnabled?: boolean | null
  }
}

export const defaultInsuranceVerificationState: InsuranceVerificationState = {}

const initialState: AppState = {
  onboarding: {
    insuranceVerificationState: defaultInsuranceVerificationState,
    timeZone: SYSTEM_US_TIMEZONE,
  },
}

export type SetVerificationStateItem = {
  key: string
  state: InsuranceVerification
}

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setReturnUrl: (
      state,
      {
        payload,
      }: PayloadAction<{
        returnUrl: string | null
        returnType?: AppState['onboarding']['returnType']
      }>,
    ) => {
      state.onboarding.returnUrl = payload.returnUrl
      state.onboarding.returnType = payload.returnType
    },
    setTimeZone:(
      state,
      { payload }: PayloadAction<string>,
    ) =>{
      state.onboarding.timeZone = payload
    },
    setProgramCode: (
      state,
      { payload }: PayloadAction<string | null>,
    ) => {
      state.onboarding.programCode = payload
    },
    setFormsApiEnabled: (
      state,
      { payload }: PayloadAction<boolean | null>,
    ) => {
      state.onboarding.formsApiEnabled = payload
    },
    setOptionalSections: (
      state,
      { payload }: PayloadAction<OptionalOnboardingFormSection[]>,
    ) => {
      state.onboarding.optionalOnboardingFormSections = payload
    },
    setSubmissionId: (state, { payload }: PayloadAction<string | null>) => {
      state.onboarding.submissionId = payload
    },
    setCaptchaToken: (state, { payload }: PayloadAction<string | null>) => {
      state.onboarding.captchaToken = payload
    },
    setVerificationState: (
      state,
      { payload }: PayloadAction<SetVerificationStateItem[]>,
    ) => {
      for (const { key, state: payloadItem } of payload) {
        const verificationState = state.onboarding.insuranceVerificationState
        const currentVerificationStateItem = verificationState[key]
        // set to null or set with status
        verificationState[key] = {
          ...currentVerificationStateItem,
          ...payloadItem,
        }
      }
    },
  },
})

export const { actions, reducer, name: reducerPath } = appSlice

export type AppActionPayloads = ReturnType<
  (typeof actions)[keyof typeof actions]
>

export default appSlice
